/**
 * ChatActions — glass pill button for a single action chip.
 *
 * Restyled per handoff `global-chat.jsx` ThreadAnaka chip row:
 *   - `all: unset` base → pill shape (radius 9999)
 *   - Glass surface: `var(--chat-surface)` + `1px solid var(--chat-border)` +
 *     `blur(20px)` + `var(--chat-shadow)`
 *   - 11.5px / weight 500 / `var(--foreground)` text
 *   - Padding 5px 12px
 *
 * The iconSize / iconName API is preserved for backward-compatibility with
 * ChatActionsCarousel and ChatThread. When rendered in the thread chip row
 * context (from ConversationTranscript / ChatThread), `iconSize` will often
 * be `'none'`; the icon is rendered when present.
 *
 * Pure props → markup. Carousel layout, ordering, and dispatching live in
 * ChatActionsCarousel; this component just renders a clickable chip button.
 */

import { resolveSnapshotActionIcon } from '@/components/v2/shared/snapshotActionIcons';

import { cn } from '@/lib/utils';

export interface ChatActionsProps {
  iconSize: 'L' | 'S' | 'none';
  iconName?: string;
  title: string;
  description: string;
  onClick: () => void;
  className?: string;
  /**
   * Visual variant.
   * - `glass` (default) — blurred glass surface; used for snapshot-level pills.
   * - `outlined` — transparent background with a 1px border; used for per-turn
   *   next-action pills so they read as lighter suggestions vs. primary actions.
   * - `primary` — filled accent CTA; the single primary call-to-action (019
   *   FR-028, e.g. the scoping "Lock the scope & start research" action), made
   *   visually distinct from the suggestion pills.
   */
  variant?: 'glass' | 'outlined' | 'primary';
}

export function ChatActions({
  iconSize,
  iconName,
  title,
  description,
  onClick,
  className,
  variant = 'glass',
}: ChatActionsProps) {
  const Icon = iconSize === 'none' ? null : resolveSnapshotActionIcon(iconName);

  const surfaceStyle =
    variant === 'glass'
      ? {
          background: 'var(--chat-surface)',
          border: '1px solid var(--chat-border)',
          backdropFilter: 'blur(20px)',
          boxShadow: 'var(--chat-shadow)',
        }
      : variant === 'primary'
        ? {
            background: 'var(--primary, #7C3AED)',
            border: '1px solid transparent',
            boxShadow: 'var(--chat-shadow)',
          }
        : {
            background: 'transparent',
            border: '1px solid var(--chat-border)',
          };

  return (
    <button
      type="button"
      onClick={onClick}
      data-variant={variant}
      className={cn(
        'cursor-pointer rounded-[9999px] text-left transition-opacity',
        'text-[11.5px] leading-none',
        variant === 'primary' ? 'font-semibold text-white' : 'font-medium text-foreground',
        'hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/50',
        iconSize === 'none' ? 'px-3 py-[5px]' : 'px-3.5 py-[6px]',
        className,
      )}
      style={surfaceStyle}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        {Icon && iconSize === 'S' ? (
          <Icon
            data-testid="chat-actions-icon"
            className="size-3.5 shrink-0 text-primary"
            aria-hidden="true"
          />
        ) : null}
        {Icon && iconSize === 'L' ? (
          <Icon
            data-testid="chat-actions-icon"
            className="size-4 shrink-0 text-primary"
            aria-hidden="true"
          />
        ) : null}
        <span className="min-w-0 truncate">{title}</span>
      </span>
      {/* description is available to screen readers / tooltip but hidden visually
          to keep the pill compact — chip row is purely the title */}
      <span className="sr-only">{description}</span>
    </button>
  );
}
